import React from 'react';
import Header from '../components/Header';

const RefundsCancellationsPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header title="Refunds & Cancellations" showBack={true} />
      
      <div className="container mx-auto px-4 py-6">
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="prose prose-sm max-w-none">
            <div className="mb-6">
              <p className="text-gray-600 text-sm mb-4">
                Last updated: {new Date().toLocaleDateString('en-IN')}
              </p>
              <p className="text-gray-700">
                This policy outlines the terms and conditions for refunds and cancellations for Minto Mobile Repair services.
              </p>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <h3 className="text-red-800 font-semibold mb-2">🚨 Important Notice</h3>
              <p className="text-red-700 text-sm">
                <strong>All refunds are subject to company approval and are processed only after thorough review by our management team. No automatic refunds are processed.</strong>
              </p>
            </div>

            <section className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">1. Refund Policy Overview</h2>
              <ul className="space-y-2 text-gray-700">
                <li>• <strong>No automatic refunds:</strong> All refund requests require manual approval from Minto Mobile Repair management</li>
                <li>• <strong>Case-by-case evaluation:</strong> Each refund request is individually reviewed based on specific circumstances</li>
                <li>• <strong>Processing time:</strong> Approved refunds take 7-14 business days to process</li>
                <li>• <strong>Refund method:</strong> Refunds are processed through the same payment method used for the original transaction</li>
              </ul>
            </section>

            <section className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">2. Cancellation Policy</h2>
              
              <div className="mb-4">
                <h3 className="font-semibold text-gray-900 mb-2">2.1 Before Technician Dispatch</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• Cancellation allowed up to 30 minutes before scheduled appointment</li>
                  <li>• Full refund may be considered (subject to company approval)</li>
                  <li>• Cancellation fee of ₹50 may apply for repeated cancellations</li>
                </ul>
              </div>

              <div className="mb-4">
                <h3 className="font-semibold text-gray-900 mb-2">2.2 After Technician Dispatch</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• Cancellation charges of ₹200 will be deducted from refund</li>
                  <li>• Remaining amount refund subject to company approval</li>
                  <li>• Transportation costs are non-refundable</li>
                </ul>
              </div>

              <div className="mb-4">
                <h3 className="font-semibold text-gray-900 mb-2">2.3 After Service Commencement</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• No cancellation allowed once diagnosis/repair work begins</li>
                  <li>• Partial refund may be considered only in exceptional circumstances</li>
                  <li>• Decision entirely at company's discretion</li>
                </ul>
              </div>
            </section>

            <section className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">3. Refund Eligibility Criteria</h2>
              
              <div className="mb-4">
                <h3 className="font-semibold text-gray-900 mb-2">3.1 Valid Refund Scenarios</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• Service not provided due to company's inability to fulfill</li>
                  <li>• Technician no-show without prior notification</li>
                  <li>• Duplicate payment processing errors</li>
                  <li>• Service quality issues verified by company investigation</li>
                  <li>• Warranty-related failures within specified period</li>
                </ul>
              </div>

              <div className="mb-4">
                <h3 className="font-semibold text-gray-900 mb-2">3.2 Non-Refundable Scenarios</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• Customer unavailability at scheduled time</li>
                  <li>• Incomplete or incorrect address provided</li>
                  <li>• Customer refusal to allow technician entry</li>
                  <li>• Device issues discovered after service completion</li>
                  <li>• Change of mind after service completion</li>
                  <li>• Damage caused by customer misuse</li>
                </ul>
              </div>
            </section>

            <section className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">4. Refund Process</h2>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h3 className="text-blue-800 font-semibold mb-2">Step-by-Step Process</h3>
              </div>
              
              <ol className="space-y-3 text-gray-700">
                <li>
                  <strong>1. Submit Request:</strong>
                  <ul className="mt-1 ml-4 space-y-1">
                    <li>• Email: support@mintomein.in</li>
                    <li>• Phone: +91 9324 299 258</li>
                    <li>• Include order number and reason for refund</li>
                  </ul>
                </li>
                <li>
                  <strong>2. Initial Review:</strong>
                  <ul className="mt-1 ml-4 space-y-1">
                    <li>• Request acknowledged within 24 hours</li>
                    <li>• Preliminary eligibility assessment</li>
                  </ul>
                </li>
                <li>
                  <strong>3. Investigation:</strong>
                  <ul className="mt-1 ml-4 space-y-1">
                    <li>• Detailed review of service records</li>
                    <li>• Technician feedback collection</li>
                    <li>• Customer statement verification</li>
                  </ul>
                </li>
                <li>
                  <strong>4. Management Decision:</strong>
                  <ul className="mt-1 ml-4 space-y-1">
                    <li>• Final approval/rejection by management</li>
                    <li>• Decision communicated within 5-7 business days</li>
                  </ul>
                </li>
                <li>
                  <strong>5. Processing:</strong>
                  <ul className="mt-1 ml-4 space-y-1">
                    <li>• Approved refunds processed within 7-14 business days</li>
                    <li>• Refund confirmation sent via email/SMS</li>
                  </ul>
                </li>
              </ol>
            </section>

            <section className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">5. Partial Refunds</h2>
              <ul className="space-y-2 text-gray-700">
                <li>• May be offered when full refund is not justified</li>
                <li>• Calculated based on service completion percentage</li>
                <li>• Deductions for parts used, labor, and transportation</li>
                <li>• Final amount determined by company management</li>
              </ul>
            </section>

            <section className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">6. Dispute Resolution</h2>
              <ul className="space-y-2 text-gray-700">
                <li>• Escalation to senior management for unresolved issues</li>
                <li>• Mediation attempts before legal proceedings</li>
                <li>• All disputes subject to Mumbai jurisdiction</li>
                <li>• Company's decision is final in most cases</li>
              </ul>
            </section>

            <section className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">7. Important Terms</h2>
              <div className="bg-gray-50 rounded-lg p-4">
                <ul className="space-y-2 text-gray-700">
                  <li>• <strong>Processing fees:</strong> Bank/payment gateway charges are non-refundable</li>
                  <li>• <strong>Time limit:</strong> Refund requests must be made within 30 days of service</li>
                  <li>• <strong>Documentation:</strong> All refund requests require proper documentation</li>
                  <li>• <strong>Communication:</strong> Official communication only through registered channels</li>
                </ul>
              </div>
            </section>

            <section className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">8. Contact for Refunds</h2>
              <div className="bg-minto-50 border border-minto-200 rounded-lg p-4">
                <p className="text-minto-800 font-medium mb-2">For Refund Requests:</p>
                <p className="text-gray-700">Email: support@mintomein.in</p>
                <p className="text-gray-700">Phone: +91 9324 299 258</p>
                <p className="text-gray-700">Business Hours: 9:00 AM - 7:00 PM (Monday to Saturday)</p>
                <p className="text-gray-700 mt-2 text-sm">
                  <strong>Note:</strong> Please include your order number, contact details, and detailed reason for refund request.
                </p>
              </div>
            </section>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
              <p className="text-yellow-800 text-sm">
                <strong>Disclaimer:</strong> This policy is subject to change without prior notice. The company reserves the right to modify refund terms based on business requirements and regulatory changes.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundsCancellationsPage; 